import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { DirectUpload } from '@rails/activestorage';

class FileUploadWithProgress {
  constructor(
    fileId,
    file,
    organizationId,
    category,
    vendorNames,
    onProgressUpdate,
    onComplete,
    onError,
  ) {
    this.file = file;
    this.organizationId = organizationId;
    this.category = category;
    this.vendorNames = vendorNames;
    this.onProgressUpdate = onProgressUpdate;
    this.onComplete = onComplete;
    this.onError = onError;
    this.id = fileId;

    this.directUpload = new DirectUpload(
      file,
      `/organizations/${organizationId}/vault/direct_uploads`,
      this,
    );
  }

  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener('progress', event => {
      const percentage = Math.round((event.loaded / event.total) * 100);
      this.onProgressUpdate(this.id, percentage);
    });
  }

  async start() {
    try {
      const blob = await new Promise((resolve, reject) => {
        this.directUpload.create((error, _blob) => {
          if (error) {
            reject(error);
          } else {
            resolve(_blob);
          }
        });
      });

      const response = await fetch('/api/client_admin/vault_files', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          vault_file: {
            file: blob.signed_id,
            vault_category_id: this.category,
            vendor_names: this.vendorNames,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      this.onComplete(this.id);
    } catch (error) {
      this.onError(this.id, error);
    }
  }
}

export const useMultipleFileUpload = () => {
  const queryClient = useQueryClient();
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const { name: userName } = useSelector(state => state.currentUser);

  const uploadFiles = async (
    files,
    organizationId,
    categoryId = null,
    categoryLabel = null,
    vendorNames = null,
  ) => {
    if (!files || files.length === 0) return;

    const uploads = files.map(file => {
      const uploadId = `upload-${Date.now()}-${Math.random()}`;

      const initialState = {
        id: uploadId,
        file,
        progress: 0,
        status: 'uploading',
        error: null,
        nameWithExtension: file.name,
        createdAt: 'Uploading...',
        createdBy: userName,
        category: categoryLabel,
        vaultCategoryId: categoryId,
        vendorNames,
      };

      setUploadingFiles(prev => [...prev, initialState]);

      const fileUpload = new FileUploadWithProgress(
        uploadId,
        file,
        organizationId,
        categoryId,
        vendorNames,
        (id, progress) => {
          setUploadingFiles(prev =>
            prev.map(item => (item.id === id ? { ...item, progress } : item)),
          );
        },
        id => {
          setUploadingFiles(prev =>
            prev.map(item =>
              item.id === id
                ? { ...item, status: 'completed', progress: 100 }
                : item,
            ),
          );

          setTimeout(() => {
            setUploadingFiles(prev => prev.filter(item => item.id !== id));
            queryClient.invalidateQueries(['vaultFiles']);
          }, 1000);
        },
        (id, error) => {
          setUploadingFiles(prev =>
            prev.map(item =>
              item.id === id
                ? { ...item, status: 'error', error: error.message }
                : item,
            ),
          );
        },
      );

      return fileUpload;
    });

    uploads.forEach(upload => upload.start());
  };

  return { uploadFiles, uploadingFiles, setUploadingFiles };
};
